import type { SetStateAction, Dispatch } from "react";
import { useCallback } from "react";
import { useEngineClient } from "../../../../services/engineClient";
import type { RecordJobData } from "@smartsuite/automation-engine-api";

export const useRecordJobData = ({
  recordJobData,
  setRecordJobData,
}: {
  recordJobData: Record<string, RecordJobData>;
  setRecordJobData: Dispatch<SetStateAction<Record<string, RecordJobData>>>;
}): {
  fetchRecordJobData: (
    recordJobId: string,
    fallbackRecordJobId?: string,
  ) => Promise<void>;
} => {
  const engineClient = useEngineClient();

  // Stable between fetches so callbacks derived from it (and the memoized
  // component trees they feed) don't rebuild on unrelated re-renders.
  const fetchRecordJobData = useCallback(
    async (
      recordJobId: string,
      fallbackRecordJobId?: string,
    ): Promise<void> => {
      if (recordJobData[recordJobId]) {
        return;
      }

      const store = (data: RecordJobData): void => {
        // Always keyed by the REQUESTED id, so renderers keep one lookup key
        // no matter which path scheme the engine actually wrote.
        setRecordJobData((prev: Record<string, RecordJobData>) => ({
          ...prev,
          [recordJobId]: data,
        }));
      };

      try {
        const res = await engineClient.getRecordJobDetails({
          recordJobId,
        });

        if (res.recordJobData) {
          store(res.recordJobData);
        }
      } catch (error) {
        // Executors disagree on where a branch-in-loop action's record jobs
        // live, so a not_found here may just mean this action wrote the other
        // scheme. Only the CALLER knows the shape (and therefore whether a
        // fallback row would be this action's own), so it supplies the id --
        // deriving it from the path would also match shapes whose plain row
        // belongs to a different iteration, or to a coordinator.
        if (
          fallbackRecordJobId === undefined ||
          (error as { code?: string }).code !== "not_found"
        ) {
          console.error("Failed to fetch record job details:", error);
          return;
        }

        try {
          const res = await engineClient.getRecordJobDetails({
            recordJobId: fallbackRecordJobId,
          });

          if (res.recordJobData) {
            store(res.recordJobData);
            return;
          }

          console.error(
            `Record job ${recordJobId} not found, and its fallback ${fallbackRecordJobId} returned no data`,
          );
        } catch (fallbackError) {
          console.error(
            `Failed to fetch record job details for ${recordJobId} (not found) and its fallback ${fallbackRecordJobId}:`,
            fallbackError,
          );
        }
      }
    },
    [recordJobData, setRecordJobData, engineClient],
  );

  return { fetchRecordJobData };
};
